import React from "react";

import { Button, InputSearch, Picto, Tabs } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";

import SearchService from "@/services/SearchService";

import { useActiveView } from "@/hooks/useActiveView";

import { LatestTasks } from "@/components/organisms";
import { PageLayout } from "@/components/templates";

import {
	clearRecentSearches,
	pushRecentSearch,
	readRecentSearches,
} from "@/utils/URecentSearches";
import { computeSearchResults } from "@/utils/USearch";

import {
	VIEW_SEARCH_GROUP,
	PROJECT_SEARCH_GROUP,
	TASK_SEARCH_GROUP,
} from "@/constants/CSearch";

import {
	TView,
	TFormatedSearchGroups,
	TSearchCategory,
	TSelectedSearchOption,
} from "@/types";

import { errorToast } from "@/modules/index";

import { SearchResultRow } from "./SearchResultRow";

const ALL_TAB = "all";
type TSearchTab = typeof ALL_TAB | TSearchCategory;

const searchUrl = (query: string, type?: string) =>
	`/search?${type ? `type=${type}&` : ""}query=${encodeURIComponent(query)}`;

export const Search: React.FC = () => {
	const [results, setResults] = React.useState<TFormatedSearchGroups[]>([]);
	const [recent, setRecent] = React.useState<string[]>(readRecentSearches);
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const query = params.get("query") ?? "";
	const type = params.get("type") ?? "";
	// The landing page's latest tasks: the view the home page shows.
	const { view } = useActiveView();

	const [value, setValue] = React.useState<string>(query);

	const handleSelect = (_value: unknown) => {
		const value = _value as TSelectedSearchOption;

		// A task opens on the home page (/tasks/:id is rewritten under its
		// view, and the panel moves the period to its days); a project
		// or a view opens its management page - the header's search
		// bar, on the home page, filters the list with them instead.
		let url = "/";
		if (value?.group === TASK_SEARCH_GROUP) url = `/tasks/${value.id}`;
		else if (value?.group === PROJECT_SEARCH_GROUP)
			url = `/projects/${value.id}`;
		else if (value?.group === VIEW_SEARCH_GROUP) url = `/views/${value.id}`;

		navigate(url);
	};

	// "All" is a digest: ten hits per type, each section linking to its
	// own tab where the type gets the full page.
	const maxResults = type ? 100 : 10;

	// SearchBar is an AsyncSelect wrapper (its own dropdown of results) —
	// this page renders results itself, full-page, so it never needed
	// SearchBar's dropdown at all (v1's `menu={false}` escape hatch for
	// exactly that). A plain debounced InputSearch driving its own fetch
	// is the real shape of what this page needs.
	// Only the latest search may show its results: a slow answer to an
	// older keyword (or to one since cleared) would otherwise land on top.
	const latestSearch = React.useRef(0);
	const fetchResults = React.useCallback(
		(val: string) => {
			const id = ++latestSearch.current;
			if (!val) {
				setResults([]);
				return;
			}
			errorToast(
				SearchService.search({
					keyword: val,
					maxTasks: maxResults,
					maxProjects: maxResults,
					maxViews: maxResults,
				}).then((resp) => {
					if (id !== latestSearch.current) return;
					setResults(computeSearchResults(resp));
					setRecent(pushRecentSearch(val));
				})
			);
		},
		[maxResults]
	);

	// Landing here with `?query=` (the header's "see more" link, a group's
	// "+N" link, a bookmark) used to show the keyword in the box and no
	// result at all: only typing fetched. The URL is the source of truth
	// on arrival and whenever the type filter changes its maximum.
	React.useEffect(() => {
		setValue(query);
		fetchResults(query);
	}, [query, fetchResults]);

	const handleChange = (val: string) => {
		setValue(val);
		fetchResults(val);
	};

	const handleClearRecent = () => {
		clearRecentSearches();
		setRecent([]);
	};

	const totalOf = (label: string) =>
		results.find((g) => g.label?.toLowerCase() === label)?.total ?? 0;
	const total = results.reduce((sum, g) => sum + (g.total ?? 0), 0);

	const tabs = [
		{
			value: ALL_TAB as TSearchTab,
			label: `${t("search.everything")} · ${total}`,
		},
		...(["tasks", "projects", "views"] as const).map((category) => ({
			value: category as TSearchTab,
			label: `${t(`${category}.title`)} · ${totalOf(category)}`,
		})),
	];

	// Tabs rewrite the URL rather than a local state: the type is what
	// sets the maximum and lands from links, so it stays the truth.
	const handleTab = (tab: TSearchTab) =>
		navigate(searchUrl(value, tab === ALL_TAB ? undefined : tab), {
			replace: true,
		});

	const hasQuery = !!value;
	const hasResults = results.some((g) => g.options?.length);

	return (
		<PageLayout title={t("search.title")} isLoading={false}>
			<div className="flex w-full max-w-3xl flex-col gap-5">
				<div className="sticky top-0 z-10 -mt-4 flex flex-col gap-4 bg-white pt-4 pb-1">
					<InputSearch
						value={value}
						onChange={handleChange}
						delay={500}
						minLength={1}
						size="lg"
						autoFocus
						placeholder={t("search.globalPH")}
					/>
					{hasQuery && (
						<Tabs<TSearchTab>
							items={tabs}
							value={(type as TSearchTab) || ALL_TAB}
							onChange={handleTab}
						/>
					)}
				</div>

				{!hasQuery && (
					<SearchLanding
						recent={recent}
						onClearRecent={handleClearRecent}
						view={view}
					/>
				)}

				{hasQuery && !hasResults && (
					<p className="py-8 text-center text-sm text-neutral-500">
						{t("search.noResults", { search: value })}
					</p>
				)}

				{results.map((group) => {
					const category = group.label?.toLowerCase() ?? "";
					if (!group.options?.length) return null;
					if (type && category !== type) return null;

					const remaining =
						(group?.total ?? 0) - group.options.length;

					return (
						<section
							key={group.label}
							className="flex flex-col gap-1"
							data-testid={`search-group-${category}`}
						>
							<div className="flex items-baseline gap-2.5 px-3 pb-1">
								<h2 className="text-base font-medium">
									{t(`${category}.title`)}
								</h2>
								<span className="text-xs text-neutral-500">
									{group.total}
								</span>
								{!type && remaining > 0 && (
									<NavLink
										to={searchUrl(value, category)}
										replace={true}
										className="ml-auto flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-800"
									>
										{t(`search.seeAll.${category}`, {
											count: group.total,
										})}
										<Picto
											icon="chevron"
											className="h-3.5 w-3.5"
										/>
									</NavLink>
								)}
							</div>
							{group.options.map((option) => (
								<SearchResultRow
									key={option.value}
									data={option}
									onSelect={handleSelect}
								/>
							))}
							{!!type && remaining > 0 && (
								<p className="pt-2 text-center text-xs text-neutral-400">
									{t("search.andMore", { count: remaining })}
								</p>
							)}
						</section>
					);
				})}
			</div>
		</PageLayout>
	);
};

interface ISearchLandingProps {
	recent: string[];
	onClearRecent: () => void;
	view?: TView;
}

/**
 * The page before any keyword: what the search covers, the last keywords
 * typed on this browser, and the latest tasks of the view the home
 * page shows, so the page is never a lone input box.
 */
const SearchLanding: React.FC<ISearchLandingProps> = ({
	recent,
	onClearRecent,
	view,
}) => (
	<div className="flex flex-col gap-6 pt-2">
		<div className="flex flex-col gap-1">
			<p className="text-lg font-medium">{t("search.landingTitle")}</p>
			<p className="text-sm text-neutral-500">
				{t("search.landingHint")}
			</p>
		</div>

		{recent.length > 0 && (
			<section
				className="flex flex-col gap-2"
				data-testid="recent-searches"
			>
				<div className="flex items-baseline">
					<span className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
						{t("search.recent")}
					</span>
					<Button
						size="sm"
						variant="ghost"
						className="ml-auto"
						onClick={onClearRecent}
					>
						{t("search.clearRecent")}
					</Button>
				</div>
				<div className="flex flex-wrap gap-2">
					{recent.map((keyword) => (
						<NavLink
							key={keyword}
							to={searchUrl(keyword)}
							className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-100"
						>
							<Picto icon="clock" className="h-3.5 w-3.5" />
							{keyword}
						</NavLink>
					))}
				</div>
			</section>
		)}

		{view && <LatestTasks view={view} />}
	</div>
);
